import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { KollelFormData } from '../schemas/kollelFormSchema';
import CheckboxMultiSelect from '@/components/ui/CheckboxMultiSelect/CheckboxMultiSelect';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import SupportedDtoIn from '@/models/in/SupportedDtoIn';
import { KollelDtoIn } from '@/models/in/KollelDtoIn';
import KollelTransferConfirmDialog from './KollelTransferConfirmDialog';

interface KollelAvrechsStepProps {
  fundId?: number;
  /** Every other kollel of this fund - an avrech already assigned to one of them is still offered
   * here (HAT-1446 phase 7), but selecting them asks for transfer confirmation first instead of
   * being silently added or silently blocked. */
  otherKollels: KollelDtoIn[];
}

/**
 * Step 2 of 4 — "שיוך אברכים לכולל": a searchable checkbox list of the fund's own supporteds.
 * The Figma spec's richer filtered picker (with inline add-supported rows) is left for a later
 * pass; CheckboxMultiSelect, already used by the phase-2 edit form, covers the same job.
 */
const KollelAvrechsStep = ({ fundId, otherKollels }: KollelAvrechsStepProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<KollelFormData>();

  const { data: supporteds } = useGetAuthApi<SupportedDtoIn[]>(fundId ? `funds/${fundId}/supporteds` : '');
  const avrechs = watch('avrechs') ?? [];
  const selectedIds = avrechs.map((a) => a.supportedId);

  const [pendingTransfer, setPendingTransfer] = useState<{ supportedId: number; kollelId: number; kollelName: string } | null>(null);

  /** supportedId -> the OTHER kollel they are currently registered in, if any (HAT-1446 phase 7). */
  const elsewhereById = useMemo(() => {
    const map = new Map<number, { kollelId: number; kollelName: string }>();
    otherKollels.forEach((k) => k.avrechs.forEach((a) => map.set(a.supportedId, { kollelId: k.id, kollelName: k.name })));
    return map;
  }, [otherKollels]);

  const options = useMemo(
    () =>
      (supporteds ?? []).map((s) => {
        const elsewhere = elsewhereById.get(s.id);
        return {
          value: s.id,
          label: `${s.firstName} ${s.lastName}`.trim(),
          sublabel: elsewhere
            ? t('kollels.wizard.step2.registeredElsewhere', { kollel: elsewhere.kollelName })
            : s.israeliId,
        };
      }),
    [supporteds, elsewhereById, t],
  );

  const commitAdd = (id: number, transfer?: { kollelId: number; kollelName: string }) => {
    const supported = (supporteds ?? []).find((s) => s.id === id);
    setValue(
      'avrechs',
      [
        ...avrechs,
        {
          supportedId: id,
          firstName: supported?.firstName ?? '',
          lastName: supported?.lastName ?? '',
          transferFromKollelId: transfer?.kollelId,
          transferFromKollelName: transfer?.kollelName,
        },
      ],
      { shouldValidate: true },
    );
  };

  const handleChange = (ids: number[]) => {
    // Removal (or a change that doesn't add anything new) always applies immediately.
    const addedId = ids.find((id) => !selectedIds.includes(id));
    if (addedId === undefined) {
      setValue('avrechs', avrechs.filter((a) => ids.includes(a.supportedId)), { shouldValidate: true });
      return;
    }

    const elsewhere = elsewhereById.get(addedId);
    if (elsewhere) {
      setPendingTransfer({ supportedId: addedId, ...elsewhere });
      return; // Held back until the coordinator approves/rejects the transfer dialog.
    }

    commitAdd(addedId);
  };

  return (
    <div className="w-full flex flex-col gap-4">
      <h3 className="heading-h3-semibold text-(--font-primary)">{t('kollels.wizard.step2.title')}</h3>
      <CheckboxMultiSelect
        options={options}
        selected={selectedIds}
        onChange={handleChange}
        emptyText={t('kollels.form.avrechsEmptyText')}
        maxHeightClass="max-h-96"
      />

      {pendingTransfer && (
        <KollelTransferConfirmDialog
          open
          avrechName={
            `${(supporteds ?? []).find((s) => s.id === pendingTransfer.supportedId)?.firstName ?? ''} ${(supporteds ?? []).find((s) => s.id === pendingTransfer.supportedId)?.lastName ?? ''}`.trim()
          }
          fromKollelName={pendingTransfer.kollelName}
          toKollelName={watch('name') ?? ''}
          onApprove={() => {
            commitAdd(pendingTransfer.supportedId, { kollelId: pendingTransfer.kollelId, kollelName: pendingTransfer.kollelName });
            setPendingTransfer(null);
          }}
          onReject={() => setPendingTransfer(null)}
        />
      )}
    </div>
  );
};

export default KollelAvrechsStep;
